import { X } from 'lucide-react-native';
import { useEffect } from 'react';

import type { ReferenceSheetProps } from '@/components/chat/bubbles/references/ReferenceSheet';
import {
  ReferenceSheetBody,
  ReferenceSheetHeaderActions,
  ReferenceSheetPager,
} from '@/components/chat/bubbles/references/ReferenceSheetBody';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Modal, ModalBackdrop, ModalCloseButton, ModalContent } from '@/components/ui/modal';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

/**
 * Web counterpart of the native reference sheet: a centered modal over the
 * same body, with arrow keys paging between references and Escape closing,
 * since a desktop reader has no swipe.
 */
export function ReferenceSheet({
  references,
  openIndex,
  onIndexChange,
  onClose,
  onOpenSource,
  viewerIsAuthor,
}: ReferenceSheetProps) {
  const isOpen = openIndex !== null && openIndex < references.length;

  useEffect(() => {
    if (openIndex === null) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft' && openIndex > 0) {
        onIndexChange(openIndex - 1);
      } else if (event.key === 'ArrowRight' && openIndex < references.length - 1) {
        onIndexChange(openIndex + 1);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [openIndex, references.length, onClose, onIndexChange]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalBackdrop />
      <ModalContent className="w-full max-w-xl overflow-hidden" aria-label="References">
        <HStack className="w-full items-center justify-between px-3 pb-3 pt-3">
          <ReferenceSheetHeaderActions
            reference={isOpen ? references[openIndex] : undefined}
            onOpenSource={onOpenSource}
          />
          <ModalCloseButton
            hitSlop={ICON_BUTTON_HIT_SLOP}
            {...getAccessibilityProps({ label: 'Close references', role: 'button' })}>
            <Icon as={X} className="text-typography-700" size="md" />
          </ModalCloseButton>
        </HStack>
        {isOpen ? (
          <>
            <ReferenceSheetBody
              references={references}
              index={openIndex}
              viewerIsAuthor={viewerIsAuthor}
            />
            <ReferenceSheetPager
              references={references}
              index={openIndex}
              onIndexChange={onIndexChange}
            />
          </>
        ) : null}
      </ModalContent>
    </Modal>
  );
}
